import React from "react";

import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import { Avatar, Box, ButtonBase, Fab, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { NavLink } from "react-router-dom";
import { makeStyles, withStyles } from "tss-react/mui";

import { ClientListQuery } from "@/gql/graphql";
import BillingStatus from "@/pages/Clients/ClientDetails/Index/components/BillingStatus";

import pinnedClients from "./store/PinnedClients";
import { useSidebarContext } from "../../Sidebar";

interface Props {
  client: ClientListQuery["clients"]["nodes"][0];
  onPin: (id: string) => void;
  onUnpin: (id: string) => void;
}

const useStyles = makeStyles()((theme) => ({
  link: {
    textDecoration: "none",
    color: theme.palette.common.black,
  },

  avatar: {
    width: "40px",
    height: "40px",
  },

  pinButton: {
    flexShrink: 0,
    width: 36,
    height: 36,
  },
}));

const StyledButtonBase = withStyles(ButtonBase, (theme) => ({
  root: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: theme.spacing(2, 2),
    "&:hover": {
      backgroundColor: "#EDF0F5",
      cursor: "pointer",
    },
  },
}));

const ClientItem: React.FC<Props> = observer(({ client, onPin, onUnpin }) => {
  const { classes } = useStyles();
  const { dispatch } = useSidebarContext();
  const pinned = pinnedClients.has(client.id);

  return (
    <NavLink
      to={`/clients/${client?.id}/profile`}
      key={client?.id}
      className={classes.link}
      onClick={() =>
        dispatch({
          type: "close-component",
          payload: "clients",
        })
      }
    >
      <StyledButtonBase component="div">
        <Fab
          className={classes.pinButton}
          color={pinned ? "primary" : "default"}
          size="small"
          onClick={(e) => {
            // Prevents click event of this button from triggering
            // the navigation to client profile and closing of
            // client list sidebar.
            e.preventDefault();
            e.stopPropagation();

            if (pinned) onUnpin(client.id);
            else onPin(client.id);
          }}
          sx={{ marginRight: 3 }}
        >
          <PushPinOutlinedIcon fontSize="small" />
        </Fab>
        <Avatar
          src={client?.profile?.avatarUrl ?? undefined}
          className={classes.avatar}
          alt={"User"}
          imgProps={{
            loading: "lazy",
          }}
        />
        <Box
          marginLeft="12px"
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          textAlign="start"
        >
          <Typography variant="body1">{client?.profile?.fullName}</Typography>
          <Typography variant="body2" color="textSecondary">
            {client?.programForDate?.name ?? "No plan"}
          </Typography>
        </Box>
        <Box display="flex" alignSelf="stretch" marginLeft="auto">
          <BillingStatus status={client?.status} />
        </Box>
      </StyledButtonBase>
    </NavLink>
  );
});

export default ClientItem;
